/**
 * Layout-preset helpers for dockview.
 *
 * Each preset is a pure function that takes the dockview API and rearranges
 * all currently open panels into a target group structure. The helpers operate
 * imperatively (addGroup + movePanel) rather than fromJSON so existing panel
 * components keep their component instance and internal state.
 *
 * Distribution rule for N groups + M panels: contiguous partitioning where
 * the first `M % N` groups get one extra panel. Examples:
 *   2 panels into 2 → [1, 1]
 *   3 panels into 2 → [2, 1]
 *   4 panels into 2 → [2, 2]
 *   5 panels into 2 → [3, 2]
 *   5 panels into 3 → [2, 2, 1]
 *   7 panels into 3 → [3, 2, 2]
 *
 * Why contiguous (not round-robin): keeps adjacent tabs visually adjacent in
 * the resulting layout, matching how users think about left/right ordering.
 */

/**
 * Compute partition sizes for distributing M items across N buckets.
 * Returns an array of bucket sizes that sum to M, with at most a 1-item
 * difference between the largest and smallest bucket.
 */
export function partitionSizes(itemCount, bucketCount) {
  const base = Math.floor(itemCount / bucketCount);
  const remainder = itemCount % bucketCount;
  const sizes = [];
  for (let i = 0; i < bucketCount; i++) {
    sizes.push(base + (i < remainder ? 1 : 0));
  }
  return sizes;
}

/**
 * Move all panels into the first existing group, leaving a single group.
 * No-op when there are 0 or 1 panels.
 *
 * Note: dockview v5 exposes movement via `panel.api.moveTo({ group })`,
 * not a top-level `api.movePanel`.
 */
export function resetToSingleGroup(api) {
  const panels = [...api.panels];
  if (panels.length <= 1) return;

  const firstGroup = panels[0].group;
  panels.forEach((panel) => {
    if (panel.group !== firstGroup) {
      panel.api.moveTo({ group: firstGroup });
    }
  });
}

/**
 * Distribute all open panels across `groupCount` groups laid out in the given
 * direction. Order of panels is preserved using contiguous partitioning.
 *
 * @param {object} api - dockview API
 * @param {number} groupCount - target number of groups (>= 2)
 * @param {'rows'|'columns'} orientation - 'rows' splits below, 'columns' splits right
 */
export function applySplit(api, groupCount, orientation) {
  const panels = [...api.panels];
  if (panels.length < 2 || groupCount < 2) return;

  // Cap the split count at the number of panels — splitting into more
  // groups than panels would leave empty groups.
  const count = Math.min(groupCount, panels.length);

  // Step 1: collapse to a single group so we have a clean starting layout.
  resetToSingleGroup(api);

  // Re-read panels after the collapse — references are stable but we want
  // the post-reset ordering.
  const orderedPanels = [...api.panels];
  const direction = orientation === 'rows' ? 'below' : 'right';

  // Step 2: create N-1 additional groups beside the first one.
  const groups = [orderedPanels[0].group];
  for (let i = 1; i < count; i++) {
    const newGroup = api.addGroup({
      referenceGroup: groups[i - 1],
      direction,
    });
    groups.push(newGroup);
  }

  // Step 3: distribute panels using contiguous partitioning.
  const sizes = partitionSizes(orderedPanels.length, count);
  let cursor = 0;
  for (let g = 0; g < count; g++) {
    const targetGroup = groups[g];
    for (let i = 0; i < sizes[g]; i++) {
      const panel = orderedPanels[cursor++];
      if (panel.group !== targetGroup) {
        panel.api.moveTo({ group: targetGroup });
      }
    }
  }
}

/**
 * Top-level entry point — apply a named preset.
 *
 * Presets:
 *   'reset'   - all panels in one group
 *   'rows-2'  - 2 groups stacked vertically
 *   'cols-2'  - 2 groups side-by-side
 *   'rows-3'  - 3 groups stacked vertically
 *   'cols-3'  - 3 groups side-by-side
 */
export function applyLayoutPreset(api, preset) {
  switch (preset) {
    case 'reset':
      return resetToSingleGroup(api);
    case 'rows-2':
      return applySplit(api, 2, 'rows');
    case 'cols-2':
      return applySplit(api, 2, 'columns');
    case 'rows-3':
      return applySplit(api, 3, 'rows');
    case 'cols-3':
      return applySplit(api, 3, 'columns');
    default:
      console.warn(`[layoutPresets] Unknown preset: ${preset}`);
  }
}
